import type { z } from 'zod';
import type {
  AllIndexableFiltersSchema,
  FilterNotifications,
  IndexableFiltersCategoriesSchema,
} from '../../schemas/indexableFiltersSchema';

import trashCan from '@ingka/ssr-icon/paths/trash-can';
import pencil from '@ingka/ssr-icon/paths/pencil';

export const LS_KEY = 'rc-ifm-notification-dismissed';

export const FILTER_EDITOR_ROLE = 'RC_FILTERS_ZZ';

export type FILTER_TAB_VIEWS = 'all' | 'market';

export type FiltersData = z.infer<typeof AllIndexableFiltersSchema>;

export type CatalogCategory = z.infer<
  typeof IndexableFiltersCategoriesSchema
>['categories'][number];

export type CatalogCategories = Record<string, CatalogCategory>;

export type CategoryData = {
  categoryId: string;
  externalCategoryId: string;
  title: string;
  imageUrl: string | null;
  childCategories: CategoryData[] | [];
};

export type FilterNotification = Omit<
  z.infer<typeof FilterNotifications>[number],
  'affectedCategories'
>;

export type NotificationDismissedStorage = {
  categoryId: string;
  notificationType: string;
}[];

export const tranformCategories = ({
  catalogue,
  categories,
}: z.infer<typeof IndexableFiltersCategoriesSchema>) => {
  const allCategories = categories.reduce<CatalogCategories>((map, obj) => {
    map[obj.externalCategoryId || ''] = obj;
    return map;
  }, {});

  const categoriesTree = createTree(catalogue.topCategories, allCategories);

  return { categories: allCategories, tree: categoriesTree };
};

export const createTree = (
  categories?: string[],
  allCategories?: CatalogCategories,
): CategoryData[] => {
  if (!categories || !allCategories) return [];

  const categoriesTree = categories
    .filter((f) => !!allCategories[f])
    .map((m) => {
      const getTree = (externalCategoryId: string): CategoryData => {
        const childCategories =
          allCategories[externalCategoryId]?.childCategories.map(getTree);

        return {
          categoryId: allCategories[externalCategoryId].categoryId,
          externalCategoryId: allCategories[externalCategoryId].externalCategoryId,
          imageUrl: allCategories[externalCategoryId].productImage || '',
          title:
            allCategories[externalCategoryId]?.categoryNameTranslation ||
            allCategories[externalCategoryId].globalCategoryName,
          childCategories,
        };
      };
      return getTree(m);
    });

  return categoriesTree;
};

export const notificationTypeMap: {
  [key: string]: { title: string; iconPath: () => React.SVGProps<SVGElement>[] };
} = {
  CATEGORY_DELETED: {
    title: 'Category deleted',
    iconPath: trashCan,
  },
  CATEGORY_RENAMED_IN_DRAFT: {
    title: 'Category renamed in draft catalogue',
    iconPath: pencil,
  },
  CATEGORY_RENAMED_IN_ACTIVE: {
    title: 'Category renamed in active catalogue',
    iconPath: pencil,
  },
};
